import { getOptionsGeneric } from "pages/ALI/ALIForm";
import { ALIRecordRequest } from "services/ALIRecord";
import { Msag } from "services/MSAGRecord";
import { Autocomplete } from "./Autocomplete";

export interface AvailableOptions {
  MCN: string[];
  STN: string[];
  STS: string[];
  STA: string[];
}

export interface LastFiredState {
  MCN?: string | Date;
  STN?: string | Date;
  STS?: string | Date;
  STA?: string | Date;
}

export interface ValidationErrors {
  MCN?: string;
  STN?: string;
  STA?: string;
}

export const validateAutocompleteField = (
  field: string,
  value: string,
  availableOptions: AvailableOptions,
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>,
) => {
  const options = availableOptions[field as keyof typeof availableOptions];
  if (
    !options.some(
      (option: string) => option.toLowerCase() === value.toLowerCase(),
    )
  ) {
    setValidationErrors((prev: ValidationErrors) => ({
      ...prev,
      [field]: `Please select a value from the list`,
    }));
    return false;
  }
  setValidationErrors((prev: ValidationErrors) => ({
    ...prev,
    [field]: undefined,
  }));
  return true;
};

// Render Methods
export const renderAutocompleteField = (
  field: keyof AvailableOptions,
  label: string,
  optionType: string,
  recordRequest: ALIRecordRequest,
  setRecordRequest: React.Dispatch<React.SetStateAction<ALIRecordRequest>>,
  lastMSAGRecord: Msag | null,
  setLastMSAGRecord: React.Dispatch<React.SetStateAction<Msag | null>>,
  availableOptions: AvailableOptions,
  setAvailableOptions: React.Dispatch<React.SetStateAction<AvailableOptions>>,
  lastFired: LastFiredState,
  validationErrors: ValidationErrors,
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>,
  showValidationErrorOnEmpty = false,
  doNotShowErrors = false,
  autoPopulateOnMsagMatch = false,
) => (
  <div>
    <Autocomplete
      label={label}
      value={recordRequest[field as keyof ALIRecordRequest] as string}
      onChange={(value: string) => {
        setRecordRequest((prev: ALIRecordRequest) => ({
          ...prev,
          [field]: value,
        }));
        validateAutocompleteField(
          field,
          value,
          availableOptions,
          setValidationErrors,
        );
      }}
      optionFetch={async () => {
        // if the field isnt sts pass setlastmsag
        const options = await getOptionsGeneric(
          optionType,
          recordRequest,
          field !== "STS" ? setLastMSAGRecord : undefined,
        );
        setAvailableOptions((prev) => ({
          ...prev,
          [field]: options,
        }));
        return options;
      }}
      lastFired={lastFired[field]}
      scale="s"
      labelSize="s"
      onTabPress={() => {
        const options = availableOptions[field];
        // if sta, mcn, and stn are filled out and the stn is focused
        // use the last msag record to auto fill the street suffix and prefix directional
        if (
          autoPopulateOnMsagMatch &&
          field === "STN" &&
          recordRequest.STA &&
          recordRequest.MCN &&
          recordRequest.STN
        ) {
          if (lastMSAGRecord) {
            if (Array.isArray(lastMSAGRecord)) {
              // If it's an array, find common values across all records
              const prefixDirs = lastMSAGRecord
                .map((record) => record.PrefixDir)
                .filter(Boolean);
              const streetSuffixes = lastMSAGRecord
                .map((record) => record.StreetSuffix)
                .filter(Boolean);
              const postDirs = lastMSAGRecord
                .map((record) => record.PostDir)
                .filter(Boolean);
              const esns = lastMSAGRecord
                .map((record) => record.esn)
                .filter(Boolean);
              const exchanges = lastMSAGRecord
                .map((record) => record.Exchange)
                .filter(Boolean);
              const generalUses = lastMSAGRecord
                .map((record) => record.GeneralUse)
                .filter(Boolean);

              const commonPrefixDir =
                prefixDirs.length > 0 &&
                prefixDirs.every((dir) => dir === prefixDirs[0])
                  ? prefixDirs[0]
                  : "";
              const commonStreetSuffix =
                streetSuffixes.length > 0 &&
                streetSuffixes.every((suffix) => suffix === streetSuffixes[0])
                  ? streetSuffixes[0]
                  : "";
              const commonPostDir =
                postDirs.length > 0 &&
                postDirs.every((dir) => dir === postDirs[0])
                  ? postDirs[0]
                  : "";
              const commonEsn =
                esns.length > 0 && esns.every((esn) => esn === esns[0])
                  ? esns[0]
                  : "";
              const commonExchange =
                exchanges.length > 0 &&
                exchanges.every((exchange) => exchange === exchanges[0])
                  ? exchanges[0]
                  : "";
              const commonGeneralUse =
                generalUses.length > 0 &&
                generalUses.every((use) => use === generalUses[0])
                  ? generalUses[0]
                  : "";

              setRecordRequest((prev: ALIRecordRequest) => ({
                ...prev,
                PRD: commonPrefixDir,
                STS: commonStreetSuffix,
                POD: commonPostDir,
                lecESN: commonEsn,
                Exchange: commonExchange,
                GeneralUse: commonGeneralUse,
              }));
            } else {
              // If it's a single record
              setRecordRequest((prev: ALIRecordRequest) => ({
                ...prev,
                PRD: lastMSAGRecord.PrefixDir || "",
                STS: lastMSAGRecord.StreetSuffix || "",
                POD: lastMSAGRecord.PostDir || "",
                lecESN: lastMSAGRecord.esn || "",
                Exchange: lastMSAGRecord.Exchange || "",
                GeneralUse: lastMSAGRecord.GeneralUse || "",
              }));
            }
          }
        }

        // if there is only one value in the available options set it to it
        if (options && options.length === 1) {
          const singleValue = options[0];
          setRecordRequest((prev: ALIRecordRequest) => ({
            ...prev,
            [field]: singleValue,
          }));
          validateAutocompleteField(
            field,
            singleValue,
            availableOptions,
            setValidationErrors,
          );
        }
        // if there are more then one value and the current value is in the list set it to that value but also make it case insensitive
        else if (options && options.length > 1) {
          const currentValue = recordRequest[field as keyof ALIRecordRequest];
          const matchedValue = options.find(
            (option) =>
              option.toLowerCase() === (currentValue as string).toLowerCase(),
          );
          if (matchedValue) {
            setRecordRequest((prev: ALIRecordRequest) => ({
              ...prev,
              [field]: matchedValue,
            }));
            validateAutocompleteField(
              field,
              matchedValue,
              availableOptions,
              setValidationErrors,
            );
          }
        }
      }}
    />
    {field !== "STS" &&
      validationErrors[field] &&
      !doNotShowErrors &&
      (recordRequest[field] || showValidationErrorOnEmpty) && (
        <div
          style={{
            color: "red",
            fontSize: "12px",
            marginTop: "-10px",
          }}
        >
          {validationErrors[field]}
        </div>
      )}
  </div>
);
